import React from 'react';
import { motion } from 'framer-motion';
import { 
  CpuChipIcon, 
  CogIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const PlatformArchitecture: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Platform Architecture
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Two powerful architectures: Document Intelligence and Business Intelligence
          </motion.p>
        </div>

        {/* Two Product Architectures */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* DocFlow AI Architecture */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-purple-200"
          >
            <div className="text-center mb-6">
              <div className="bg-purple-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <DocumentTextIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">DocFlow AI</h3>
              <p className="text-purple-700 font-semibold mb-4">Document Intelligence</p>
            </div>

            <div className="space-y-4">
              {/* Layer 1 */}
              <div className="bg-white rounded-lg p-4 border-2 border-purple-300">
                <div className="font-bold text-purple-900 mb-2">📄 Document Intake</div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div>• Invoices, Contracts, Forms</div>
                  <div>• Scanned Documents & PDFs</div>
                  <div>• Multi-format Support</div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-6 h-6 text-purple-600 transform rotate-90" />
              </div>

              {/* Layer 2 */}
              <div className="bg-white rounded-lg p-4 border-2 border-purple-400">
                <div className="font-bold text-purple-900 mb-2">🔄 OCR & Extraction</div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div>• Intelligent OCR Processing</div>
                  <div>• Data Field Extraction</div>
                  <div>• Template Recognition</div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-6 h-6 text-purple-600 transform rotate-90" />
              </div>

              {/* Layer 3 */}
              <div className="bg-white rounded-lg p-4 border-2 border-purple-500">
                <div className="font-bold text-purple-900 mb-2">✅ Validation & Review</div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div>• AI Confidence Scoring</div>
                  <div>• Human-in-the-Loop Review</div>
                  <div>• Exception Handling</div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-6 h-6 text-purple-600 transform rotate-90" />
              </div>

              {/* Layer 4 */}
              <div className="bg-purple-600 text-white rounded-lg p-4">
                <div className="font-bold mb-2">📊 Structured Output</div>
                <div className="text-sm space-y-1">
                  <div>• Extracted Data & Metadata</div>
                  <div>• Workflow Automation</div>
                  <div>• System Integration</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* DB Talker Architecture */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200"
          >
            <div className="text-center mb-6">
              <div className="bg-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ChatBubbleLeftRightIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">DB Talker</h3>
              <p className="text-blue-700 font-semibold mb-4">MCP-Powered BI Chat</p>
            </div>

            <div className="space-y-4">
              {/* Layer 1 */}
              <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
                <div className="font-bold text-blue-900 mb-2">💬 Chat Interface</div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div>• Natural Language Input</div>
                  <div>• Mobile Responsive</div>
                  <div>• White-Label Branding</div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-6 h-6 text-blue-600 transform rotate-90" />
              </div>

              {/* Layer 2 */}
              <div className="bg-white rounded-lg p-4 border-2 border-blue-400">
                <div className="font-bold text-blue-900 mb-2">⚙️ Configuration Engine</div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div>• JSON-Based Config</div>
                  <div>• Data Source Setup</div>
                  <div>• Custom Prompts</div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-6 h-6 text-blue-600 transform rotate-90" />
              </div>

              {/* Layer 3 */}
              <div className="bg-white rounded-lg p-4 border-2 border-blue-500">
                <div className="font-bold text-blue-900 mb-2">🔌 MCP Adapter</div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div>• Schema Discovery</div>
                  <div>• SQL Generation</div>
                  <div>• Query Optimization</div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-6 h-6 text-blue-600 transform rotate-90" />
              </div>

              {/* Layer 4 */}
              <div className="bg-blue-600 text-white rounded-lg p-4">
                <div className="font-bold mb-2">📊 Visual Insights</div>
                <div className="text-sm space-y-1">
                  <div>• Auto-Generated Charts</div>
                  <div>• Real-Time Data</div>
                  <div>• Executive Reports</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Step-by-Step Process */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">The Intelligence Flow</h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: 1,
                title: "Data Ingestion",
                description: "Knowledge Management Interface ingests your:",
                items: ["📄 Documents with context tags", "🗂️ Knowledge base articles", "❓ FAQ databases", "📅 Data with expiry dates"]
              },
              {
                step: 2,
                title: "AI Processing",
                description: "Private AI Models process and learn:",
                items: ["🧠 Industry-specific language models", "🔄 Continuous learning from interactions", "🎯 Context-aware responses", "🔒 All processing stays private"]
              },
              {
                step: 3,
                title: "AI Apps",
                description: "Business Applications leverage the brain:",
                items: ["📧 Custom email responses", "💰 Quotation generation", "📋 Contract processing", "💬 Interactive chat interfaces"]
              },
              {
                step: 4,
                title: "External Integration",
                description: "API Connections enhance capabilities:",
                items: ["🔗 Internal system APIs", "📨 Email processing services", "🌐 External service integration", "📊 Real-time data synchronization"]
              }
            ].map((process, index) => (
              <div key={process.step} className="text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-800 to-secondary-800 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{process.title}</h4>
                <p className="text-sm text-gray-600 mb-3">{process.description}</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  {process.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Technical Benefits */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why This Architecture Works</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "🔒",
                title: "Complete Privacy",
                description: "Your Private LLM processes everything locally. No data leaves your infrastructure."
              },
              {
                icon: "🧠",
                title: "Shared Intelligence",
                description: "One AI brain powers multiple assistants. Learning from one improves all."
              },
              {
                icon: "🔄",
                title: "Continuous Learning",
                description: "Every interaction, document, and API call makes your AI brain smarter."
              },
              {
                icon: "⚡",
                title: "Scalable Architecture",
                description: "Add new assistant portals easily. Each leverages existing intelligence."
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="text-3xl mb-4">{benefit.icon}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlatformArchitecture; 